import { useState, useEffect } from 'react'
import { Plus, X, ChevronUp, ChevronDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { OutlineTemplate, OutlineTemplateSection } from '@/lib/api'

interface TemplateFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template?: OutlineTemplate | null
  initialSections?: OutlineTemplateSection[]
  initialName?: string
  onSave: (data: {
    name: string
    description?: string
    sections: OutlineTemplateSection[]
    visibility: 'private' | 'shared'
  }) => Promise<void>
  mode: 'create' | 'edit' | 'save'
}

const EMPTY_SECTION: OutlineTemplateSection = { title: '' }

export function TemplateFormDialog({
  open,
  onOpenChange,
  template,
  initialSections,
  initialName,
  onSave,
  mode,
}: TemplateFormDialogProps) {
  const [name, setName] = useState(template?.name ?? initialName ?? '')
  const [description, setDescription] = useState(template?.description ?? '')
  const [visibility, setVisibility] = useState<'private' | 'shared'>(template?.visibility ?? 'private')
  const [sections, setSections] = useState<OutlineTemplateSection[]>(
    template?.sections ?? initialSections ?? [{ ...EMPTY_SECTION }]
  )
  const [isSaving, setIsSaving] = useState(false)

  // Reset form whenever the dialog opens — ensures fresh props are picked up
  useEffect(() => {
    if (open) {
      setName(template?.name ?? initialName ?? '')
      setDescription(template?.description ?? '')
      setVisibility(template?.visibility ?? 'private')
      setSections(template?.sections ?? initialSections ?? [{ ...EMPTY_SECTION }])
    }
  }, [open, template, initialSections, initialName])

  const addSection = () => {
    setSections([...sections, { ...EMPTY_SECTION }])
  }

  const removeSection = (index: number) => {
    if (sections.length <= 1) return
    setSections(sections.filter((_, i) => i !== index))
  }

  const updateSection = (index: number, field: keyof OutlineTemplateSection, value: string | number | undefined) => {
    setSections(sections.map((s, i) => (i === index ? { ...s, [field]: value } : s)))
  }

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= sections.length) return
    const updated = [...sections]
    const a = updated[index]!
    const b = updated[newIndex]!
    updated[index] = b
    updated[newIndex] = a
    setSections(updated)
  }

  const isValid = name.trim().length > 0 && sections.length > 0 && sections.every(s => (s.title ?? '').trim().length > 0)

  const handleSave = async () => {
    if (!isValid) return
    setIsSaving(true)
    try {
      const cleanedSections: OutlineTemplateSection[] = sections.map(s => {
        const section: OutlineTemplateSection = { title: s.title.trim() }
        const desc = s.description?.trim()
        if (desc) section.description = desc
        if (s.targetWordCount) section.targetWordCount = s.targetWordCount
        return section
      })
      const trimmedDesc = description.trim()
      const saveData: {
        name: string
        description?: string
        sections: OutlineTemplateSection[]
        visibility: 'private' | 'shared'
      } = {
        name: name.trim(),
        sections: cleanedSections,
        visibility,
      }
      if (trimmedDesc) saveData.description = trimmedDesc
      await onSave(saveData)
      onOpenChange(false)
    } catch {
      // Error handled by caller
    } finally {
      setIsSaving(false)
    }
  }

  const title = mode === 'edit' ? 'Edit Template' : mode === 'save' ? 'Save as Template' : 'New Template'
  const dialogDescription = mode === 'edit'
    ? 'Update the template name, sections, and visibility.'
    : mode === 'save'
      ? 'Save your current outline as a reusable template.'
      : 'Create a reusable outline template with sections.'
  const saveLabel = mode === 'edit' ? 'Save Changes' : mode === 'save' ? 'Save Template' : 'Create Template'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4 px-1 -mx-1 flex-1 overflow-y-auto">
          {/* Name */}
          <div className="grid gap-2">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Infrastructure Response"
              maxLength={255}
            />
          </div>

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="template-description">
              Description <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What kind of bid is this template for?"
              maxLength={1000}
              rows={2}
            />
          </div>

          {/* Visibility */}
          <div className="grid gap-2">
            <Label>Visibility</Label>
            <Select value={visibility} onValueChange={(v) => setVisibility(v as 'private' | 'shared')}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="shared">Shared with organisation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sections */}
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label>Sections</Label>
              <Button variant="ghost" size="sm" onClick={addSection} className="h-7 text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Section
              </Button>
            </div>

            <div className="space-y-2">
              {sections.map((section, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-3 space-y-2 bg-muted/30"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-meta w-5 flex-shrink-0">{index + 1}.</span>
                    <Input
                      value={section.title}
                      onChange={(e) => updateSection(index, 'title', e.target.value)}
                      placeholder="Section title"
                      className="h-8 text-sm"
                      maxLength={255}
                    />
                    <Input
                      type="number"
                      value={section.targetWordCount ?? ''}
                      onChange={(e) => {
                        const val = e.target.value ? parseInt(e.target.value, 10) : undefined
                        updateSection(index, 'targetWordCount', val && val > 0 ? val : undefined)
                      }}
                      placeholder="Words"
                      className="h-8 text-sm w-24 flex-shrink-0"
                      min={1}
                    />
                    <div className="flex flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => moveSection(index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => moveSection(index, 'down')}
                        disabled={index === sections.length - 1}
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => removeSection(index)}
                        disabled={sections.length <= 1}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="pl-7">
                    <Input
                      value={section.description ?? ''}
                      onChange={(e) => updateSection(index, 'description', e.target.value || undefined)}
                      placeholder="Description (optional)"
                      className="h-7 text-xs"
                      maxLength={1000}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={!isValid || isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
